import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAccount, useConnect, useConnectors, useDisconnect, useSwitchChain } from "wagmi";
import { Copy, ExternalLink, LogOut, X as XIcon, Wallet } from "lucide-react";
import { arc } from "@/lib/chain";
import { LINKS, ARC } from "@/config";
import { useLanguage } from "@/i18n/LanguageContext";
import Typewriter from "@/components/Typewriter";

const WalletUIContext = createContext(null);

export const shortAddress = (a) => (a ? `${a.slice(0, 6)}...${a.slice(-4)}` : "");

export function WalletUIProvider({ children }) {
  const [open, setOpen] = useState(false);
  const value = useMemo(
    () => ({ open, openModal: () => setOpen(true), closeModal: () => setOpen(false) }),
    [open]
  );
  return (
    <WalletUIContext.Provider value={value}>
      {children}
      <WalletModal />
    </WalletUIContext.Provider>
  );
}

export function useWalletUI() {
  const ctx = useContext(WalletUIContext);
  if (!ctx) throw new Error("useWalletUI must be used inside WalletUIProvider");
  return ctx;
}

// Shared wallet state hook
export function useMizuWallet() {
  const { address, isConnected, chainId } = useAccount();
  const connectors = useConnectors();
  const { mutate: connect, isPending: connecting, error: connectError, reset } = useConnect();
  const { mutate: disconnect } = useDisconnect();
  const { mutate: switchChain, isPending: switching, error: switchError } = useSwitchChain();
  const wrongNetwork = isConnected && chainId !== arc.id;

  const switchToArc = () =>
    switchChain({
      chainId: arc.id,
      addEthereumChainParameter: {
        chainName: arc.name,
        nativeCurrency: arc.nativeCurrency,
        rpcUrls: [...arc.rpcUrls.default.http],
        blockExplorerUrls: [arc.blockExplorers.default.url],
      },
    });

  return {
    address,
    isConnected,
    chainId,
    connectors,
    connect,
    connecting,
    connectError,
    resetConnectError: reset,
    disconnect,
    switchToArc,
    switching,
    switchError,
    wrongNetwork,
  };
}

function WalletModal() {
  const { open, closeModal } = useWalletUI();
  const { t } = useLanguage();
  const w = useMizuWallet();
  const [copied, setCopied] = useState(false);

  // Hide the generic "Injected" fallback when no EIP-1193 provider exists at all.
  const hasProvider =
    typeof window !== "undefined" && typeof window.ethereum !== "undefined";
  const visibleConnectors = w.connectors.filter(
    (c) => c.id !== "injected" || hasProvider
  );

  useEffect(() => {
    if (open) setCopied(false);
  }, [open]);

  useEffect(() => {
    if (w.isConnected && !w.wrongNetwork && open) {
      const timer = setTimeout(closeModal, 900);
      return () => clearTimeout(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [w.isConnected, w.wrongNetwork, open]);

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(w.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="wallet-modal-overlay"
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeModal}
        >
          <motion.div
            data-testid="wallet-modal"
            className="mizu-panel mizu-corners w-full max-w-md p-0"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* title bar */}
            <div className="flex items-center justify-between border-b border-mizu-cyan/20 px-4 py-2.5">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 animate-pulse-glow bg-mizu-cyan" />
                <span className="font-mono text-xs tracking-[0.25em] text-mizu-cyan">
                  {t("wallet.title")}
                </span>
              </div>
              <button
                data-testid="wallet-modal-close"
                onClick={closeModal}
                className="p-1 text-mizu-dim transition-colors hover:text-white"
                aria-label={t("wallet.close")}
              >
                <XIcon size={16} />
              </button>
            </div>

            <div className="p-5">
              {/* terminal init readout */}
              <div className="mb-5 border border-mizu-cyan/15 bg-[#020611]/80 p-3">
                <Typewriter
                  lines={t("wallet.initLines")}
                  speed={10}
                  lineDelay={110}
                  className="font-mono text-[11px] leading-5 text-mizu-cyan/80"
                />
              </div>

              {!w.isConnected && (
                <>
                  <div className="mb-2 font-mono text-[10px] tracking-[0.3em] text-mizu-faint">
                    {t("wallet.selectWallet")}
                  </div>
                  {visibleConnectors.length === 0 ? (
                    <p
                      data-testid="wallet-none-message"
                      className="border border-mizu-cyan/15 bg-mizu-elevated/40 p-3 font-mono text-[11px] leading-5 text-mizu-dim"
                    >
                      {t("wallet.noWallet")}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {visibleConnectors.map((c) => (
                        <button
                          key={c.uid || c.id}
                          data-testid={`wallet-connect-${(c.name || c.id).toLowerCase().replace(/\s+/g, "-")}`}
                          disabled={w.connecting}
                          onClick={() => w.connect({ connector: c })}
                          className="mizu-btn flex w-full items-center justify-between border border-mizu-cyan/30 bg-mizu-elevated/40 px-4 py-3 font-mono text-xs tracking-[0.2em] text-mizu-silver disabled:opacity-50"
                        >
                          <span className="flex items-center gap-2">
                            <Wallet size={14} className="text-mizu-cyan" />
                            {c.name}
                          </span>
                          <span className="text-[10px] text-mizu-faint">
                            {w.connecting ? t("wallet.connecting") : "EIP-6963"}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {w.connectError && (
                    <p data-testid="wallet-connect-error" className="mt-3 font-mono text-[11px] text-red-400">
                      {t("wallet.rejected")}
                    </p>
                  )}
                </>
              )}

              {w.isConnected && (
                <div className="space-y-3" data-testid="wallet-connected-panel">
                  <div className="border border-mizu-cyan/15 bg-mizu-elevated/40 p-3">
                    <div className="font-mono text-[10px] tracking-[0.3em] text-mizu-faint">
                      {t("wallet.connectedAs")}
                    </div>
                    <div
                      className="mt-1 font-mono text-sm text-white"
                      data-testid="wallet-address"
                    >
                      {shortAddress(w.address)}
                    </div>
                    <div className="mt-1 flex items-center gap-2 font-mono text-[11px]">
                      <span
                        className={`h-1.5 w-1.5 ${w.wrongNetwork ? "bg-red-400" : "bg-mizu-cyan animate-pulse-glow"}`}
                      />
                      <span className={w.wrongNetwork ? "text-red-400" : "text-mizu-cyan"}>
                        {w.wrongNetwork ? `CHAIN ${w.chainId}` : ARC.networkLabel}
                      </span>
                    </div>
                  </div>

                  {w.wrongNetwork && (
                    <div className="border border-red-400/30 bg-red-950/20 p-3" data-testid="wallet-wrong-network">
                      <p className="font-mono text-xs text-red-400">{t("wallet.wrongNetwork")}</p>
                      <p className="mt-1 font-mono text-[11px] text-red-300/80">
                        {t("wallet.wrongNetworkHint")}
                      </p>
                      <button
                        data-testid="wallet-switch-arc"
                        disabled={w.switching}
                        onClick={w.switchToArc}
                        className="mizu-btn mt-3 w-full border border-mizu-cyan/50 bg-mizu-cyan/10 px-4 py-2.5 font-mono text-xs tracking-[0.25em] text-mizu-cyan disabled:opacity-50"
                      >
                        {w.switching ? t("wallet.switching") : t("wallet.switchToArc")}
                      </button>
                      {w.switchError && (
                        <p className="mt-2 font-mono text-[10px] text-red-400">
                          {w.switchError.message?.slice(0, 120)}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-2">
                    <button
                      data-testid="wallet-copy"
                      onClick={copyAddress}
                      className="mizu-btn flex items-center justify-center gap-1.5 border border-mizu-cyan/25 px-2 py-2 font-mono text-[10px] tracking-[0.15em] text-mizu-dim hover:text-white"
                    >
                      <Copy size={12} />
                      {copied ? t("wallet.copied") : t("wallet.copy")}
                    </button>
                    <a
                      data-testid="wallet-explorer"
                      href={`${LINKS.explorer}/address/${w.address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mizu-btn flex items-center justify-center gap-1.5 border border-mizu-cyan/25 px-2 py-2 font-mono text-[10px] tracking-[0.15em] text-mizu-dim hover:text-white"
                    >
                      <ExternalLink size={12} />
                      {t("wallet.explorer")}
                    </a>
                    <button
                      data-testid="wallet-disconnect"
                      onClick={() => w.disconnect()}
                      className="mizu-btn flex items-center justify-center gap-1.5 border border-mizu-cyan/25 px-2 py-2 font-mono text-[10px] tracking-[0.15em] text-mizu-dim hover:text-white"
                    >
                      <LogOut size={12} />
                      {t("wallet.disconnect")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Navbar / CTA wallet button — shows address chip when connected.
export function WalletButton({ compact = false, testid = "connect-wallet-btn" }) {
  const { t } = useLanguage();
  const { openModal } = useWalletUI();
  const w = useMizuWallet();

  if (w.isConnected) {
    return (
      <button
        data-testid={testid}
        onClick={openModal}
        className="mizu-btn flex items-center gap-2 border border-mizu-cyan/40 bg-mizu-elevated/50 px-4 py-2 font-mono text-xs tracking-[0.15em] text-mizu-cyan"
      >
        <span className={`h-1.5 w-1.5 ${w.wrongNetwork ? "bg-red-400" : "bg-mizu-cyan animate-pulse-glow"}`} />
        {shortAddress(w.address)}
      </button>
    );
  }

  return (
    <button
      data-testid={testid}
      onClick={openModal}
      className="mizu-btn border border-mizu-cyan/50 bg-mizu-cyan/10 px-4 py-2 font-mono text-xs tracking-[0.2em] text-mizu-cyan"
    >
      {compact ? t("nav.connect") : t("nav.connectWallet")}
    </button>
  );
}
